import { useMemo } from "react";
import { Box, Grid, makeStyles, Typography } from "@material-ui/core";

import { theme } from "../../../../theme";
import { COLORS } from "../../../../theme/colors";
import { Icon } from "../../../../components/base";
import { NoTableData } from "../../../../components/parts";
import BadRateIcon from "../../../../assets/images/icons/badRate.svg";
import GoodRateIcon from "../../../../assets/images/icons/goodRate.svg";
import ArrowLongTopIcon from "../../../../assets/images/buildings/ArrowLongTop.svg";
import ArrowLongBottomIcon from "../../../../assets/images/buildings/ArrowLongBottom.svg";

const useStyles = makeStyles(() => ({
  main: {
    "&:not(:last-child)": {
      marginBottom: theme.spacing(0.8),
    },
  },
  barContainer: {
    backgroundColor: COLORS.darks[0][25],
    maxWidth: 450,
  },
  buildingValueBar: {
    backgroundColor: COLORS.purple,
  },
  address: {
    marginLeft: theme.spacing(0.8),
    padding: 5.5,
    color: COLORS.white,
    fontWeight: "bold",
  },
  buildingValue: {
    color: "black",
    fontSize: 18,
  },
  buildingValueBox: {
    paddingLeft: "20px",
  },
  horizontalDashLine: {
    width: "100%",
    height: 1,
    backgroundImage: `linear-gradient(to right, ${COLORS.darks[0][50]} 0%, ${COLORS.darks[0][50]} 50%, transparent 50%)`,
    backgroundSize: "10px 1px",
    backgroundRepeat: "repeat-x",
    display: "inline-flex",
  },
  avgLine: {
    position: "absolute",
    padding: "0 5px",
    left: 56,
    zIndex: 5,
    background: COLORS.white,
  },
  avgBlock: {
    display: "flex",
    position: "relative",
    alignItems: "center",
    marginBottom: 5,
    width: "100%",
  },
  aboveRate: {
    position: "absolute",
    left: 60,
    top: -60,
    width: "auto",
  },
  arrowTop: {
    width: 12,
    marginTop: 5,
  },
  belowRate: {
    position: "absolute",
    top: 15,
    left: 60,
    width: "auto",
  },
  arrowBottom: {
    width: 12,
    marginBottom: 5,
  },
  boxWrapper: {
    maxWidth: 730,
    margin: "auto",
  },
}));

const BuildingsGroup = ({ buildings, paddingRightInValue, valueSuffix }) => {
  const classes = useStyles();
  return (
    <>
      {buildings.map((building, index) => (
        <Grid
          container
          key={index}
          className={classes.main}
          justifyContent="center"
          alignItems="center"
        >
          <Grid item xs={2}></Grid>
          <Grid className={classes.barContainer} item xs={8}>
            <Grid
              className={classes.buildingValueBar}
              key={index}
              style={{
                width: building.width,
              }}
            >
              <Typography className={classes.address}>{building.name}</Typography>
            </Grid>
          </Grid>
          <Grid
            item
            xs={2}
            className={classes.buildingValueBox}
            style={{ paddingRight: paddingRightInValue }}
          >
            <Typography align="right" className={classes.buildingValue}>
              {building.value} {valueSuffix}
            </Typography>
          </Grid>
        </Grid>
      ))}
    </>
  );
};

export const ShowBars = ({ buildings, suffix }) => {
  const classes = useStyles();
  const avg = buildings.reduce((r, i) => r + i.value, 0) / (buildings.length || 1);

  const [aboveAvgBuildings, bellowAvgBuildings, paddingRightInValue] = useMemo(() => {
    const aboveBuildings = [];
    const bellowBuildings = [];
    let maxValue = 0;
    buildings.forEach((building) => {
      if (building.value >= avg) {
        aboveBuildings.push(building);
      } else {
        bellowBuildings.push(building);
      }
      if (building.value > maxValue) {
        maxValue = building.value;
      }
    });
    const maxValueLength = (maxValue + "").length;
    return [aboveBuildings, bellowBuildings, maxValueLength < 6 ? (6 - maxValueLength) * 10 : 0];
  }, [buildings, avg]);

  if (!buildings.length) return <NoTableData title="No data yet" rootStyle={{ width: "100%" }} />;

  return (
    <Grid container className={classes.boxWrapper} justifyContent="center">
      <BuildingsGroup
        buildings={aboveAvgBuildings}
        paddingRightInValue={paddingRightInValue}
        valueSuffix={suffix}
      />

      {aboveAvgBuildings.length && bellowAvgBuildings.length ? (
        <Box className={classes.avgBlock}>
          <Grid container alignItems="center" direction="column" className={classes.aboveRate}>
            <Icon src={GoodRateIcon} hoverable />
            <Icon root={classes.arrowTop} src={ArrowLongTopIcon} hoverable />
          </Grid>
          <Box className={classes.avgLine}>Avg</Box>
          <Box className={classes.horizontalDashLine}></Box>
          <Grid container alignItems="center" direction="column" className={classes.belowRate}>
            <Icon root={classes.arrowBottom} src={ArrowLongBottomIcon} hoverable />
            <Icon src={BadRateIcon} hoverable />
          </Grid>
        </Box>
      ) : null}

      <BuildingsGroup
        buildings={bellowAvgBuildings}
        paddingRightInValue={paddingRightInValue}
        valueSuffix={suffix}
      />
    </Grid>
  );
};
